import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, LinearProgress, Typography } from '@mui/material';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import api from '../config/api';
import { DOCUMENT } from '../constants/documentTheme';
import { MEMORIES_GUEST_TOKEN_KEY } from '../utils/memoriesHost';
import { prepareMemoryImage } from '../utils/prepareMemoryImage';
import { rejectCopy, MEMORIES_ACCEPT_IMAGES } from '../utils/imageSniff';
import MemoriesCameraDialog, { shouldUseNativeCameraCapture } from './MemoriesCameraDialog';

const formatMb = (bytes) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
const MEMORIES_UPLOAD_TIMEOUT_MS = 120000;

const hasFiles = (event) => Boolean(event.dataTransfer?.types?.includes?.('Files'));

const isTimeoutError = (err) => (
  err?.code === 'ECONNABORTED'
  || err?.code === 'ETIMEDOUT'
  || err?.response?.status === 408
  || err?.response?.status === 504
  || /timeout/i.test(err?.message || '')
);

const isRetryableFailure = (item) => {
  if (!item || item.status !== 'bad') return false;
  const reason = item.reason || '';
  if (['UNSUPPORTED_TYPE', 'TOO_LARGE', 'RAW_UNSUPPORTED', 'HEIC_UNSUPPORTED'].includes(reason)) {
    return false;
  }
  return Boolean(item.file) && (item.timedOut || item.retryable !== false);
};

const MemoriesUploadPanel = ({
  slug,
  storageUsedBytes,
  storageQuotaMb,
  onUploaded,
  compact = false,
}) => {
  const { t } = useTranslation();
  const libraryInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [items, setItems] = useState([]);
  const [batchIds, setBatchIds] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [batchBusy, setBatchBusy] = useState(false);
  // Phones / Appilix: library only until native camera is supported.
  // Desktop: webcam dialog + library upload.
  const allowTakePhoto = !shouldUseNativeCameraCapture();

  useEffect(() => {
    const guestToken = localStorage.getItem(MEMORIES_GUEST_TOKEN_KEY);
    if (guestToken) {
      api.defaults.headers.common['X-Guest-Token'] = guestToken;
    }
  }, []);

  useEffect(() => {
    const blockBrowserOpen = (event) => {
      event.preventDefault();
    };
    window.addEventListener('dragover', blockBrowserOpen);
    window.addEventListener('drop', blockBrowserOpen);
    return () => {
      window.removeEventListener('dragover', blockBrowserOpen);
      window.removeEventListener('drop', blockBrowserOpen);
    };
  }, []);

  const uploadOne = async (item) => {
    const file = item.file;
    try {
      const prepared = item.prepared || await prepareMemoryImage(file);
      const form = new FormData();
      const ext = (prepared.contentType || '').includes('jpeg')
        ? '.jpg'
        : (prepared.contentType || '').includes('png')
          ? '.png'
          : '.webp';
      const uploadName = prepared.originalName.replace(/\.[^.]+$/, ext);
      form.append('file', prepared.blob, uploadName);
      form.append('originalFilename', prepared.originalName);
      form.append('originalBytes', String(prepared.originalBytes));
      if (prepared.capturedAt) form.append('capturedAt', prepared.capturedAt);
      await api.post(`/memories/events/${slug}/media`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: MEMORIES_UPLOAD_TIMEOUT_MS,
      });
      return { ok: true, prepared };
    } catch (err) {
      const reason = err.reason || err.response?.data?.reason || '';
      const timedOut = isTimeoutError(err);
      return {
        ok: false,
        prepared: item.prepared || null,
        timedOut,
        reason,
        retryable: timedOut || !['UNSUPPORTED_TYPE', 'TOO_LARGE', 'RAW_UNSUPPORTED', 'HEIC_UNSUPPORTED'].includes(reason),
        error: timedOut
          ? t('memories.uploadTimedOut')
          : (err.response?.data?.error || err.message || rejectCopy(reason, file?.size)),
      };
    }
  };

  const runQueue = async (queueItems, { replace = false } = {}) => {
    if (queueItems.length === 0) return;
    const ids = queueItems.map((item) => item.id);
    setBatchBusy(true);
    setBatchIds(ids);
    if (replace) {
      setItems((prev) => {
        const byId = new Map(queueItems.map((item) => [item.id, { ...item, status: 'busy', error: null }]));
        return prev.map((item) => byId.get(item.id) || item);
      });
    } else {
      setItems((prev) => [...prev, ...queueItems.map((item) => ({ ...item, status: 'busy' }))]);
    }

    let uploadedAny = false;
    for (const queued of queueItems) {
      const result = await uploadOne(queued);
      if (result.ok) {
        uploadedAny = true;
        setItems((prev) => prev.map((item) => (
          item.id === queued.id
            ? { ...item, status: 'ok', prepared: result.prepared, error: null, timedOut: false }
            : item
        )));
      } else {
        setItems((prev) => prev.map((item) => (
          item.id === queued.id
            ? {
              ...item,
              status: 'bad',
              prepared: result.prepared,
              error: result.error,
              timedOut: result.timedOut,
              reason: result.reason,
              retryable: result.retryable,
            }
            : item
        )));
      }
    }

    setBatchBusy(false);
    setBatchIds([]);
    // Drop successes from the list — album refresh shows them in the grid.
    setItems((prev) => prev.filter((item) => item.status !== 'ok'));
    if (uploadedAny && onUploaded) onUploaded();
    if (libraryInputRef.current) libraryInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const onFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    const stamp = Date.now();
    const queueItems = files.map((file, index) => ({
      id: `${file.name || 'photo'}-${file.size}-${file.lastModified}-${index}-${stamp}`,
      name: file.name || t('memories.cameraPhoto'),
      originalBytes: file.size,
      file,
      prepared: null,
      status: 'busy',
    }));
    await runQueue(queueItems);
  };

  const retryItem = async (item) => {
    if (!isRetryableFailure(item) || batchBusy) return;
    await runQueue([{ ...item, status: 'busy', error: null }], { replace: true });
  };

  const preventNav = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const batchItems = batchIds.length
    ? items.filter((item) => batchIds.includes(item.id))
    : items;
  const uploadingTotal = batchBusy ? (batchIds.length || batchItems.length) : 0;
  const uploadingDone = batchItems.filter((item) => item.status === 'ok').length;
  const busyItems = batchItems.filter((item) => item.status === 'busy');
  const failedItems = items.filter((item) => item.status === 'bad');
  const showBatchBar = batchBusy && uploadingTotal > 0;
  const batchProgress = uploadingTotal > 0
    ? Math.min(100, Math.round(((uploadingDone + (busyItems.length ? 0.35 : 0)) / uploadingTotal) * 100))
    : 0;

  const openLibrary = (event) => {
    event.stopPropagation();
    libraryInputRef.current?.click();
  };

  const openCamera = (event) => {
    event.stopPropagation();
    if (!allowTakePhoto) return;
    setCameraOpen(true);
  };

  return (
    <Box
      sx={{
        backgroundColor: DOCUMENT.surface,
        border: `1px solid ${DOCUMENT.rule}`,
        mb: { xs: 2, md: 3 },
      }}
    >
      <Box
        data-testid="memories-dropzone"
        onDragEnter={(event) => {
          preventNav(event);
          if (hasFiles(event)) setDragActive(true);
        }}
        onDragOver={(event) => {
          preventNav(event);
          if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
        }}
        onDragLeave={(event) => {
          preventNav(event);
          if (!event.currentTarget.contains(event.relatedTarget)) setDragActive(false);
        }}
        onDrop={(event) => {
          preventNav(event);
          setDragActive(false);
          onFiles(event.dataTransfer?.files);
        }}
        sx={{
          display: 'block',
          m: { xs: 1, md: compact ? 1.5 : 2.5 },
          border: `1px dashed ${dragActive ? DOCUMENT.accent : DOCUMENT.rule}`,
          backgroundColor: dragActive ? 'rgba(184, 149, 90, 0.08)' : 'transparent',
          p: { xs: 1.25, md: compact ? 2 : 4 },
          textAlign: 'center',
        }}
      >
        <input
          ref={libraryInputRef}
          type="file"
          accept={MEMORIES_ACCEPT_IMAGES}
          multiple
          hidden
          data-testid="memories-library-input"
          onChange={(event) => onFiles(event.target.files)}
        />
        {allowTakePhoto && (
          <input
            ref={cameraInputRef}
            type="file"
            accept={MEMORIES_ACCEPT_IMAGES}
            capture="environment"
            hidden
            data-testid="memories-camera-input"
            onChange={(event) => onFiles(event.target.files)}
          />
        )}
        <Typography
          sx={{
            color: DOCUMENT.ink,
            display: { xs: 'none', md: 'block' },
            fontSize: { md: compact ? 14 : 16 },
          }}
        >
          {t('memories.dropPhotos')}
        </Typography>
        <Typography
          sx={{
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            fontSize: 11,
            color: DOCUMENT.ink3,
            mt: { xs: 0, md: 1 },
            mb: { xs: 1, md: 2 },
            display: { xs: 'none', sm: 'block' },
          }}
        >
          {t('memories.acceptedTypes')}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            justifyContent: 'center',
          }}
        >
          <Button
            type="button"
            variant="outlined"
            startIcon={<PhotoLibraryOutlinedIcon />}
            onClick={openLibrary}
            disabled={batchBusy}
            fullWidth
            sx={{
              borderColor: DOCUMENT.ruleHard,
              color: DOCUMENT.ink2,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              fontSize: 11,
              borderRadius: '2px',
              py: { xs: 1, md: 0.75 },
              maxWidth: { xs: '100%', sm: allowTakePhoto ? 220 : 280 },
            }}
          >
            {t('memories.chooseFromLibrary')}
          </Button>
          {allowTakePhoto && (
            <Button
              type="button"
              variant="outlined"
              startIcon={<PhotoCameraOutlinedIcon />}
              onClick={openCamera}
              disabled={batchBusy}
              data-testid="memories-take-photo"
              fullWidth
              sx={{
                borderColor: DOCUMENT.ruleHard,
                color: DOCUMENT.ink2,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                fontSize: 11,
                borderRadius: '2px',
                py: { xs: 1, md: 0.75 },
                maxWidth: { xs: '100%', sm: 220 },
              }}
            >
              {t('memories.takePhoto')}
            </Button>
          )}
        </Box>
        <Typography
          sx={{
            mt: 1.5,
            fontSize: 12,
            color: DOCUMENT.ink3,
            maxWidth: 360,
            mx: 'auto',
            display: { xs: 'none', md: 'block' },
          }}
        >
          {allowTakePhoto ? t('memories.cameraHint') : t('memories.uploadLibraryOnlyHint')}
        </Typography>
      </Box>

      {showBatchBar && (
        <Box sx={{ px: { xs: 1.5, md: 2.5 }, pb: 2 }} data-testid="memories-upload-progress">
          <Typography sx={{ fontSize: 13, color: DOCUMENT.ink, mb: 1 }}>
            {t('memories.uploadingProgress', { done: uploadingDone, total: uploadingTotal })}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={batchProgress}
            sx={{ '& .MuiLinearProgress-bar': { backgroundColor: DOCUMENT.accent } }}
          />
        </Box>
      )}

      {!batchBusy && failedItems.length > 0 && (
        <Box sx={{ px: { xs: 1.5, md: 2.5 }, pb: 2 }}>
          {failedItems.map((item) => (
            <Box
              key={item.id}
              sx={{
                mb: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, color: DOCUMENT.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.name}
                </Typography>
                <Typography sx={{ fontSize: 11, color: '#9C3B2A' }}>
                  {item.error}
                </Typography>
              </Box>
              {isRetryableFailure(item) && (
                <Button
                  type="button"
                  size="small"
                  onClick={() => retryItem(item)}
                  data-testid={`memories-retry-${item.id}`}
                  sx={{
                    flexShrink: 0,
                    color: DOCUMENT.oxblood,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    fontSize: 11,
                    border: `1px solid ${DOCUMENT.ruleHard}`,
                    borderRadius: '2px',
                    px: 1.25,
                  }}
                >
                  {t('memories.uploadRetry')}
                </Button>
              )}
            </Box>
          ))}
        </Box>
      )}

      <Box
        sx={{
          borderTop: `1px solid ${DOCUMENT.rule}`,
          px: { xs: 1, md: compact ? 1.5 : 2.5 },
          py: { xs: 1, md: 1.5 },
          display: 'flex',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Typography sx={{ letterSpacing: '0.12em', textTransform: 'uppercase', fontSize: 11, color: DOCUMENT.ink3 }}>
          {failedItems.length > 0 && !batchBusy
            ? t('memories.uploadFailedCount', { count: failedItems.length })
            : t('memories.uploadCount', {
              done: showBatchBar ? uploadingDone : 0,
              total: showBatchBar ? uploadingTotal : 0,
            })}
        </Typography>
        {storageUsedBytes != null && storageQuotaMb != null && (
          <Typography sx={{ letterSpacing: '0.12em', textTransform: 'uppercase', fontSize: 11, color: DOCUMENT.ink3 }}>
            {formatMb(storageUsedBytes)} / {storageQuotaMb} MB
          </Typography>
        )}
      </Box>

      {allowTakePhoto && (
        <MemoriesCameraDialog
          open={cameraOpen}
          onClose={() => setCameraOpen(false)}
          onCapture={(file) => onFiles([file])}
        />
      )}
    </Box>
  );
};

export default MemoriesUploadPanel;
